import { getVendor, VENDORS } from '../../config/vendorRegistry';
import { DUAL_CPU_FLASH_FEATURE_ENABLED } from '../../utils/dualCpuFlashFeature';
import type { DomainConfiguration } from '../ConfigurationMapper';
import type { RulesContext } from './context';

/**
 * CONF-210. The Dual CPU flash node (VCH-5500-F1N) is a gated swap for
 * the flash set's F-nodes: two processors, 2 PCIe + 1 OCP NICs, and its own
 * throughput table. It ships on AIC only.
 *
 * This module is the SINGLE definition of "may the flash set use the Dual CPU
 * node". Four consumers: the R13 business rule (which reverts an ineligible
 * selection so the UI control snaps back), resolveFlashNodeModel in
 * src/utils/flashNodeModel.js (so a stale flag can never price the 5500), the
 * metrics seam in src/metrics/index.ts (which protects the rules-free path the
 * AI optimizer takes), and the config cards (which disable the control).
 *
 * Rules never see Cognito groups, so the dualCpuAccess group (CONF-259; was
 * execAccess) is a UI gate, not one of these. A saved Dual CPU quote loaded by
 * a user without it keeps its hardware and its numbers; only the control is
 * read-only for them (user decision 2026-09-25).
 *
 * See docs/superpowers/specs/2026-09-25-conf-210-dual-cpu-flash-node-design.md.
 */

/** Whether this vendor's hardware offers the Dual CPU flash node at all. */
const vendorSupportsDualCpuFlash = (vendorId: string): boolean => {
  const upperVendorId = vendorId?.toUpperCase();
  const isKnownVendor = Object.prototype.hasOwnProperty.call(VENDORS, upperVendorId);
  if (!isKnownVendor) return false;
  const vendor = getVendor(vendorId) as any;
  return Boolean(vendor?.capabilities?.supportsDualCpuFlash);
};

/**
 * Human-readable vendor name for UI copy. Same guard as mlecRules: `getVendor`
 * warns and falls back to AIC for an unknown id, which would print "AIC" for a
 * typo'd or future vendor. Never throws.
 */
const vendorDisplayName = (vendorId: string): string => {
  const upperVendorId = vendorId?.toUpperCase();
  const isKnownVendor = Object.prototype.hasOwnProperty.call(VENDORS, upperVendorId);
  if (!isKnownVendor) return vendorId;
  const vendor = getVendor(vendorId) as any;
  return vendor?.displayName || vendorId;
};

/**
 * Whether the Dual CPU flash node may be selected for this vendor. The kill
 * switch wins over everything. Unknown vendors are ineligible rather than an
 * error — this runs on every config change and must never throw.
 */
export const dualCpuFlashEligible = (vendorId: string): boolean =>
  DUAL_CPU_FLASH_FEATURE_ENABLED && vendorSupportsDualCpuFlash(vendorId);

/** Why the Dual CPU node is unavailable, as UI copy, or null when available. */
export const dualCpuFlashIneligibleReason = (vendorId: string): string | null => {
  if (!DUAL_CPU_FLASH_FEATURE_ENABLED) {
    return 'Dual CPU flash nodes are not enabled in this release';
  }
  if (!vendorSupportsDualCpuFlash(vendorId)) {
    return `Not available on ${vendorDisplayName(vendorId)} hardware`;
  }
  return null;
};

/**
 * R13 (CONF-210). Revert the flash node model to `standard` when the current
 * vendor cannot supply the Dual CPU node.
 *
 * Only ever turns the selection OFF, and only when it is explicitly on: a
 * config with no `nodeModel` is left without one, because stamping a value
 * would move the frozen initial-config snapshots. Eligibility permits the
 * Dual CPU node; it does not select it — turning it on is always the exec's
 * action.
 *
 * Deliberately does NOTHING while the kill switch is off. The switch is a
 * rollback lever and a rollback must be reversible: every resolver already
 * treats a persisted 'dualCpu' as the single-CPU node while the feature is
 * off, so rewriting it here would only make a quote re-saved during the
 * rollback lose its Dual CPU node for good.
 *
 * Deliberately does NOT clear on AFA disable either: every other AFA field is
 * sticky across an enable/disable round trip, and the cards carry a TODO
 * about not wiping the F-node configuration.
 *
 * SHARED rule: afaNodeModel is a single flat field serving both the Base and
 * the Expansion views, so this runs for both system types. Nothing it reads
 * is written by another rule, so its position after R12 is not load-bearing.
 */
export function applyDualCpuFlashEligibility(next: DomainConfiguration, ctx: RulesContext): void {
  if (!DUAL_CPU_FLASH_FEATURE_ENABLED) return;
  const afa = next.system.afa;
  if (afa.nodeModel === 'dualCpu' && !vendorSupportsDualCpuFlash(ctx.vendorId)) {
    afa.nodeModel = 'standard';
  }
}
